import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ActivityButton } from 'components/ActivityButton'
import { Modal } from 'components/Modal/Modal'
import { useLayout } from 'hooks/useLayout'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { type Campaign } from 'types/api'
import { type RequiredId } from 'utils/typeUtils'
import { isPresent } from '@jjvgaming/player-payback-library'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { formatDateTime } from 'utils/util'
import { useEffect } from 'react'
import { EditCommunicationFiltersSchema } from './EditCommunicationFiltersSchema'
import { isNil } from 'lodash'
import { parseISO } from 'date-fns'
import { featureAccessStore } from 'stores/featureAccessStore'

interface EditCampaignFiltersProps {
  campaign: RequiredId<Campaign>
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  updateIsActive: boolean
  onSubmitCampaign: (campaign: RequiredId<Campaign>) => void
}

export type GenderType = 'Both' | 'Female' | 'Male' | 'PreferNotToAnswer'

export const EditCommunicationFiltersModal = ({
  campaign,
  updateIsActive,
  isModalOpen,
  toggleModalIsOpen,
  onSubmitCampaign,
}: EditCampaignFiltersProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const communicationsBannerMessages = featureAccessStore(
    (state) => state.communicationsBannerMessages
  )

  const formMethods = useForm({
    resolver: yupResolver(EditCommunicationFiltersSchema),
    mode: 'all',
    context: { isModalOpen },
    defaultValues: {
      filterGender: (campaign.filterGender as GenderType) ?? 'Both',
      filterAgeRange: campaign.filterAgeRange ?? undefined,
      filterState: campaign.filterState ?? undefined,
      filterZipCode: campaign.filterZipCode ?? undefined,
      filterAccountCreatedDate: getStartDate(campaign),
      filterNotCheckedInSinceDate: getNotCheckedInSinceDate(campaign),
      filterWinnersOnly: campaign.filterWinnersOnly ?? false,
      filterIncompleteProfiles: campaign.filterIncompleteProfiles ?? false,
    },
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  // Reset form values when sweepstake changes or modal opens/closes
  useEffect(() => {
    reset({
      filterGender: (campaign.filterGender as GenderType) ?? 'Both',
      filterAgeRange: campaign.filterAgeRange ?? undefined,
      filterState: campaign.filterState ?? undefined,
      filterZipCode: campaign.filterZipCode ?? undefined,
      filterAccountCreatedDate: getStartDate(campaign),
      filterNotCheckedInSinceDate: getNotCheckedInSinceDate(campaign),
      filterWinnersOnly: campaign.filterWinnersOnly ?? false,
      filterIncompleteProfiles: campaign.filterIncompleteProfiles ?? false,
    })
  }, [campaign, isModalOpen, reset])

  const handleSubmitWrapper = handleSubmit((data) => {
    onSubmitCampaign({
      ...campaign,
      filterGender: data.filterGender,
      filterAgeRange: data.filterAgeRange,
      filterState: data.filterState,
      filterZipCode: data.filterZipCode,
      filterWinnersOnly: data.filterWinnersOnly,
      filterIncompleteProfiles: data.filterIncompleteProfiles,
      filterAccountCreatedDate: data.filterAccountCreatedDate
        ? formatDateTime(String(data.filterAccountCreatedDate))
        : null,
      filterNotCheckedInSinceDate: data.filterNotCheckedInSinceDate
        ? formatDateTime(String(data.filterNotCheckedInSinceDate))
        : null,
    })
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper}>
          <ModalFullHeader
            title="Edit Message Filters"
            toggleIsOpen={toggleModalIsOpen}
          />
          <ModalFullBody heightModal={heightModal}>
            <Grid container>
              <Grid container>
                {Object.keys(errors).length > 0 && (
                  <Grid item xs={12} pb={4}>
                    <Alert severity="error">
                      Please correct errors to continue
                      {Object.keys(errors)}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12} md={6} paddingTop={3}>
                  <InputLabel
                    htmlFor="filterGender"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Gender
                  </InputLabel>
                </Grid>
                <Grid item xs={12} pt={1} md={6}>
                  <Controller
                    {...register('filterGender')}
                    defaultValue={campaign.filterGender ?? 'Both'}
                    render={({ field }) => (
                      <Select {...field} fullWidth placeholder="Gender">
                        <MenuItem value="Both">Both</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="PreferNotToAnswer">
                          Prefer Not To Answer
                        </MenuItem>
                      </Select>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6} paddingTop={3}>
                  <InputLabel
                    htmlFor="filterAgeRange"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Age Range
                  </InputLabel>
                </Grid>
                <Grid item xs={12} pt={1} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Enter Age Range i.e. 21-35"
                    {...register('filterAgeRange')}
                    defaultValue={'filterAgeRange'}
                    helperText="5 characters max"
                    error={isPresent(errors.filterAgeRange)}
                  ></TextField>
                  {errors.filterAgeRange?.message && (
                    <FormFieldErrorMessage
                      message={errors.filterAgeRange.message}
                    />
                  )}
                </Grid>

                {communicationsBannerMessages && (
                  <>
                    <Grid item xs={12} md={6} paddingTop={3}>
                      <InputLabel
                        htmlFor="filterState"
                        sx={{
                          fontSize: '16px',
                          fontWeight: '100',
                          width: '100%',
                        }}
                      >
                        State
                      </InputLabel>
                    </Grid>

                    <Grid item xs={12} pt={1} md={6}>
                      <TextField
                        fullWidth
                        multiline={true}
                        placeholder="Enter State(s) of check-in i.e. IL or IL,IN"
                        {...register('filterState')}
                        defaultValue={'filterState'}
                        helperText="200 characters max"
                        error={isPresent(errors.filterState)}
                      ></TextField>
                      {errors.filterState?.message && (
                        <FormFieldErrorMessage
                          message={errors.filterState.message}
                        />
                      )}
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6} paddingTop={3}>
                  <InputLabel
                    htmlFor="filterZipCode"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Zip Code
                  </InputLabel>
                </Grid>

                <Grid item xs={12} pt={1} md={6}>
                  <TextField
                    fullWidth
                    multiline={true}
                    placeholder="Enter Zip Code(s) of check-in i.e. 61920 or 61920,62565"
                    {...register('filterZipCode')}
                    defaultValue={'filterZipCode'}
                    helperText="500 characters max"
                    error={isPresent(errors.filterZipCode)}
                  ></TextField>
                  {errors.filterZipCode?.message && (
                    <FormFieldErrorMessage
                      message={errors.filterZipCode.message}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6} paddingTop={3}>
                  <InputLabel
                    htmlFor="filterAccountCreatedDate"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Account Created Date (on or before)
                  </InputLabel>
                </Grid>
                <Grid item xs={12} pt={1} md={6}>
                  <AdornedDateTimeField
                    name="filterAccountCreatedDate"
                    control={control}
                  />
                </Grid>

                {communicationsBannerMessages && (
                  <>
                    <Grid item xs={12} md={6} paddingTop={3}>
                      <InputLabel
                        htmlFor="filterNotCheckedInSinceDate"
                        sx={{
                          fontSize: '16px',
                          fontWeight: '100',
                          width: '100%',
                        }}
                      >
                        Not Checked In Since Date (on or before)
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} pt={1} md={6}>
                      <AdornedDateTimeField
                        name="filterNotCheckedInSinceDate"
                        control={control}
                      />
                    </Grid>
                  </>
                )}

                {communicationsBannerMessages && (
                  <>
                    <Grid item xs={12} md={6} paddingTop={2}>
                      <InputLabel
                        id="filterWinnersOnly"
                        sx={{
                          fontSize: '16px',
                          fontWeight: '100',
                          width: '100%',
                        }}
                      >
                        Winners Only
                      </InputLabel>
                    </Grid>

                    <Grid item xs={12} pt={1} md={6}>
                      <Controller
                        name="filterWinnersOnly"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            label=""
                            control={
                              <Checkbox
                                style={{ height: '40px' }}
                                checked={field.value}
                                onChange={(e) =>
                                  field.onChange(e.target.checked)
                                }
                              />
                            }
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6} paddingTop={2}>
                  <InputLabel
                    htmlFor="filterIncompleteProfiles"
                    sx={{ fontSize: '16px', fontWeight: '100', width: '100%' }}
                  >
                    Incomplete Profiles
                  </InputLabel>
                </Grid>

                <Grid item xs={12} pt={1} md={6}>
                  <Controller
                    name="filterIncompleteProfiles"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        label=""
                        control={
                          <Checkbox
                            style={{ height: '40px' }}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButton
              disabled={errors && Object.keys(errors).length > 0}
              active={!!updateIsActive}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}

const getStartDate = (campaign: Campaign) => {
  if (isNil(campaign.filterAccountCreatedDate)) {
    return undefined
  }

  return parseISO(campaign.filterAccountCreatedDate)
}

const getNotCheckedInSinceDate = (campaign: Campaign) => {
  if (isNil(campaign.filterNotCheckedInSinceDate)) {
    return undefined
  }

  return parseISO(campaign.filterNotCheckedInSinceDate)
}
